import type { OpenCheckboxGroupProps } from '@healthnz-prm/fhir-engine-renderer/types'
import { ChangeEvent, useEffect, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Add01Icon, Cancel01Icon } from '@hugeicons/core-free-icons'
import { InputFieldset } from '../inputFieldset'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

interface OpenValueProps {
  onClear: () => void
  value: string
}

function OpenValue({ onClear, value }: OpenValueProps) {
  return (
    <div className='mb-2 flex items-center gap-2'>
      <Button
        type='button'
        variant='ghost'
        size='icon-sm'
        onClick={onClear}
        aria-label={`Remove open answer: "${value}"`}
      >
        <HugeiconsIcon icon={Cancel01Icon} />
      </Button>
      <span className='wrap-break-word' aria-label={`Open answer: "${value}"`}>
        {value}
      </span>
    </div>
  )
}

interface OpenInputProps {
  onAdd: (value: string) => boolean
  error: string | undefined
}

function OpenInput({ onAdd, error }: OpenInputProps) {
  const [privateValue, setPrivateValue] = useState('')

  function handleOnAdd() {
    const success = onAdd(privateValue)

    if (success) {
      setPrivateValue('')
    }
  }

  return (
    <div className='flex items-center gap-2'>
      <Input
        type='text'
        aria-label='Open answer'
        aria-invalid={!!error}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setPrivateValue(e.target.value)
        }
        value={privateValue}
      />
      <Button
        type='button'
        variant='ghost'
        size='icon-sm'
        onClick={handleOnAdd}
        aria-label={`Add open-answer: "${privateValue}"`}
      >
        <HugeiconsIcon icon={Add01Icon} />
      </Button>
    </div>
  )
}

export default function OpenCheckboxGroup(props: OpenCheckboxGroupProps) {
  const {
    inputId,
    onAdd,
    onRemove,
    onRemoveByIndex,
    openLabel = 'Other',
    options,
    value
  } = props

  const [privateError, setPrivateError] = useState<string>()

  useEffect(() => {
    if (privateError) {
      const timeout = setTimeout(() => setPrivateError(undefined), 3000)

      return () => clearTimeout(timeout)
    }
  }, [privateError])

  const handleOptionChangeFor =
    (optionValue: number) => (checked: boolean | 'indeterminate') => {
      if (checked === true) {
        onAdd(optionValue)
      } else {
        onRemove(optionValue)
      }
    }

  function handleOnClearFor(index: number) {
    return () => onRemoveByIndex(index)
  }

  function handleAddOpen(valueToAdd: string) {
    if (valueToAdd === '') {
      setPrivateError("Can't add an empty answer")
      return false
    }

    if (value?.includes(valueToAdd)) {
      setPrivateError(`${valueToAdd} is already selected`)
      return false
    }

    onAdd(valueToAdd)
    return true
  }

  return (
    <InputFieldset {...props} errorMessage={privateError}>
      {options.map(option => (
        <div className='flex items-center gap-3' key={option.value}>
          <Checkbox
            id={`${inputId}-${option.value}`}
            aria-labelledby={`${inputId}-${option.value}-label`}
            checked={value?.includes(option.value) ?? false}
            onCheckedChange={handleOptionChangeFor(option.value)}
          />
          <Label
            id={`${inputId}-${option.value}-label`}
            htmlFor={`${inputId}-${option.value}`}
            className='font-normal'
          >
            {option.label}
          </Label>
        </div>
      ))}
      <Label className='font-normal'>{openLabel}</Label>
      {value?.map((v, index) => {
        if (typeof v === 'number') return null
        return (
          <OpenValue key={index} value={v} onClear={handleOnClearFor(index)} />
        )
      })}
      <OpenInput onAdd={handleAddOpen} error={privateError} />
    </InputFieldset>
  )
}
